import {useElectricityPricesDataQuery, type DatetimeValue, Electricity} from '@domain/electricity'
import {
  useCurrentScheduleQuery,
  fetchCurrentSchedule,
  getCurrentScheduleQueryKey
} from '@domain/schedule'
import type {Schedule} from '@domain/schedule'
import {getUtcOffset} from '@hconnect/common/utils'
import {useRequiredUser} from '@hconnect/login'
import {
  dateFormatter,
  timeFormatter,
  formatCurrency,
  formatMW,
  datetimeFormatter,
  dataTestId
} from '@hconnect/uikit/src/common'
import {Alert, LoadingButton} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone, useUpmPlantContext} from '@hconnect/upm'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {ArrowForward} from '@mui/icons-material'
import {
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import {useQueryClient} from '@tanstack/react-query'
import {
  getLocalizedDayMonthTimeFormat,
  useDisplayPlantCurrency,
  usePlanningChartStartEnd
} from '@widgets/planning-schedule-chart'
import moment, {Moment} from 'moment-timezone'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../../consts'
import {useTrackPlannerEvent} from '../../../helpers'
import {useConfirmDialog, useScheduleCostAvoidanceDialog, useSubmitSchedule} from '../../../hooks'
import {adjustEnLocale} from '../../../selectors'

export interface ElectricityPurchaseConfirmationProps {
  show: boolean
  onClose: () => void
  electricity: Electricity
  submissionInfo: Exclude<Schedule['submissionInfo'], undefined>
}

function getElectricityPrice(
  prices: DatetimeValue<Moment>[],
  timeslot: string
): number | undefined {
  return prices.find((p) => p.datetime.toISOString() === timeslot)?.value
}

export const ElectricityPurchaseConfirmation: React.FC<ElectricityPurchaseConfirmationProps> = ({
  show,
  onClose,
  electricity,
  submissionInfo
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const {plantId: plantCode} = useUpmPlantContext()
  const loggedUser = useRequiredUser()
  const queryClient = useQueryClient()

  const {upForPurchasing, planned: plannedPowerConsumption} = electricity
  const timezoneId = useUpmPlantTimezone()
  const displayCurrency = useDisplayPlantCurrency()
  const {startOfPlan, endOfPlan} = usePlanningChartStartEnd()
  const {data: electricityPrices} = useElectricityPricesDataQuery({
    plantId: plantCode,
    timezoneId,
    range: [startOfPlan, endOfPlan]
  })
  const {isFetching: isScheduleFetching} = useCurrentScheduleQuery({
    plantId: plantCode,
    range: [startOfPlan, endOfPlan]
  })

  const trackPlannerEvent = useTrackPlannerEvent()
  const {plantId} = useUpmPlantContext()

  const isCostAvoidanceEnabled = useCommonFeatureFlag(FeatureFlagKey.CostAvoidance, {
    plantId
  })
  const {setIsDialogOpen} = useScheduleCostAvoidanceDialog()

  const openCostAvoidanceDialog = useMemo(
    () => (isCostAvoidanceEnabled ? () => setIsDialogOpen(true) : undefined),
    [setIsDialogOpen, isCostAvoidanceEnabled]
  )

  const {mutate: submitSchedule} = useSubmitSchedule(openCostAvoidanceDialog)

  const {openDialog} = useConfirmDialog()

  const powerConsumptionDict = useMemo(() => {
    return Object.fromEntries(
      plannedPowerConsumption.map((entry) => [entry.dateTimeIso, entry.power])
    )
  }, [plannedPowerConsumption])

  // price in forecast is in price per megawatt; but we use 15minutes slots
  const purchaseData = useMemo(() => {
    if (!electricityPrices) {
      return null
    }
    return upForPurchasing.map(({dateTimeIso, dateTimeUTC}) => {
      return {
        price: getElectricityPrice(electricityPrices.forecast, dateTimeIso),
        // we need to convert power for 15 min interval to energy in MWh
        power: powerConsumptionDict[dateTimeIso],
        // PFMnet uses right aligned intervals (so the interval 00:15 is the first of the day) but internally we use left-aligned
        datetimeUTC: dateTimeUTC.clone().add(15, 'minutes')
      }
    })
  }, [upForPurchasing, electricityPrices, powerConsumptionDict])

  const handleSubmitPlan = (schedule: Schedule) => {
    trackPlannerEvent({name: 'onSubmitPlanClick'})
    submitSchedule({
      plantCode,
      scheduleId: schedule.scheduleId,
      updatedById: loggedUser.user_id
    })
  }

  const getElectricityTranslationKey = (key: string) => `electricity.${key}`
  const getTranslation = (key: string) => t(getElectricityTranslationKey(key))

  const handleReject = () => onClose()

  const handleConfirm = async () => {
    const schedule = await queryClient.fetchQuery({
      queryKey: getCurrentScheduleQueryKey(plantCode, [startOfPlan, endOfPlan]),
      queryFn: ({signal}) =>
        fetchCurrentSchedule({plantId: plantCode, range: [startOfPlan, endOfPlan], signal}),
      staleTime: 0
    })
    if (schedule.lastSaved.savedById !== loggedUser.user_id) {
      openDialog({
        testId: 'someone_else_working_on_the_plan_warning',
        title: t('planning.someoneElseWorkingOnThePlanTitle'),
        mainAction: {
          icon: <ArrowForward />,
          text: t('common.submit'),
          onAction: () => {
            handleSubmitPlan(schedule)
            onClose()
          }
        },
        additionalContent: (
          <>
            <Alert
              accentColor="warning"
              content={t('planning.someoneElseWorkingOnThePlanWarning', {
                user: schedule.lastSaved.savedBy,
                date: getLocalizedDayMonthTimeFormat(
                  moment.utc(schedule.lastSaved.savedAt),
                  timezoneId,
                  language
                )
              })}
            />
            <Typography variant="body1" sx={{mt: 2}}>
              {t('common.doYouWantToContinue')}
            </Typography>
          </>
        )
      })
    } else {
      onClose()
      handleSubmitPlan(schedule)
    }
  }

  if (!purchaseData) {
    return null
  }

  return (
    <Dialog
      sx={{maxHeight: '70vh'}}
      open={show}
      fullWidth
      maxWidth="md"
      onClose={handleReject}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle
        id="alert-dialog-title"
        {...dataTestId('electricity-purchase-confirmation-modal')}
      >
        {getTranslation('submitPlan')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <Typography>{getTranslation('electricityToBePurchasedLabel')}</Typography>
          <Typography>
            {t(getElectricityTranslationKey('submittingDateRange'), {
              from: datetimeFormatter(moment.utc(submissionInfo.start).tz(timezoneId), language),
              to: datetimeFormatter(moment.utc(submissionInfo.end).tz(timezoneId), language)
            })}
          </Typography>
        </DialogContentText>
      </DialogContent>
      <TableContainer component={Paper}>
        <Table sx={{maxWidth: 650}} size="small">
          <TableHead>
            <TableRow>
              <TableCell align="left">{getTranslation('periodOfTime')}</TableCell>
              <TableCell align="left">{getTranslation('power')}</TableCell>
              <TableCell align="left">{getTranslation('price')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {purchaseData.map((item) => (
              <TableRow key={`${item.datetimeUTC.toISOString()}`}>
                <TableCell
                  component="th"
                  scope="row"
                  {...dataTestId('electricity-purchasing-confirmation-datetime-cell')}
                >
                  {dateFormatter(
                    item.datetimeUTC,
                    adjustEnLocale(language),
                    getUtcOffset(timezoneId, item.datetimeUTC)
                  )}
                  {', '}
                  {t('electricity.displayTime', {
                    time: timeFormatter(
                      item.datetimeUTC,
                      language,
                      getUtcOffset(timezoneId, item.datetimeUTC)
                    )
                  })}
                </TableCell>
                <TableCell>{formatMW(item.power, language)}</TableCell>
                <TableCell>
                  {t('common.amountInCurrencyPerMWh', {
                    amount: item.price !== undefined ? formatCurrency(item.price, 2, language) : '',
                    currency: displayCurrency
                  })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <DialogActions>
        <Button
          onClick={handleReject}
          variant="outlined"
          color="secondary"
          disabled={isScheduleFetching}
        >
          {t('common.cancel')}
        </Button>
        <LoadingButton
          isLoading={isScheduleFetching}
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          {...dataTestId('electricity-purchasing-confirmation-modal-ok')}
        >
          {t('common.confirm')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
